/**
 * Popover component module.
 * @module shared/components/Popover
 */
import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {usePopper} from 'react-popper';
import styled from 'styled-components/macro';
import {v4 as uuid} from 'uuid';
import {fill, fillMaxWidth} from './cssUtils';

const REGEX_TOP = /^top/;
const REGEX_RIGHT = /^right/;
const REGEX_BOTTOM = /^bottom/;
const REGEX_LEFT = /^left/;

const _TargetDiv = styled.div`
  ${({display}) => `display: ${display || 'inline-block'};`};
  ${props => props.width ? fill(props.width) : ''}
  ${props => props.maxWidth ? fillMaxWidth(props.maxWidth) : ''}
  ${props => props.margin ? `margin: ${props.margin};` : ''}
`;

const _PopperDiv = styled.div.attrs(props => ({
  bgColor: props.theme.dark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
  fgColor: props.theme.dark ? '#ffffff' : '#000000',
}))`
  background-color: transparent;
  cursor: default;
  z-index: 9999;
`;

const _PopperInnerDiv = styled.div.attrs(props => ({
  bgColor: props.theme.dark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
  fgColor: props.theme.dark ? '#ffffff' : '#000000',
}))`
  padding: 5px;
  border-radius: 4px;
  backdrop-filter: blur(4px);
  font-size: 12px;
  z-index: 1;
  position: relative;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.5);
  color: ${({fgColor}) => fgColor};
  background-color: ${({bgColor}) => bgColor};
  ${({maxWidth}) => maxWidth ? fill(maxWidth) : ''}
  ${({height}) => height ? `height: ${height};` : ''}
  ${({width}) => width ? `width: ${width};` : ''}
  ${({alignment}) => alignment ? `text-align: ${alignment};` : ''}
  ${props => props.theme ? `font-family: ${props.theme.bodyText.name};` : ''}
`;

const _ArrowDiv = styled.div.attrs(props => ({
  bgColor: props.theme.dark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)',
}))`
  position: absolute;
  backdrop-filter: blur(4px);
  position: relative;
  z-index: 2;
  ${props => {
    const {
      placement,
      bgColor,
    } = props;

    if (REGEX_TOP.test(placement)) {
      return `
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 5px solid ${bgColor};
        bottom: -5px;
      `;
    }

    if (REGEX_BOTTOM.test(placement)) {
      return `
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-bottom: 5px solid ${bgColor};
        top: -5px;
      `;
    }

    if (REGEX_LEFT.test(placement)) {
      return `
        border-top: 5px solid transparent;
        border-bottom: 5px solid transparent;
        border-left: 5px solid ${bgColor};
        right: -5px;
      `;
    }

    if (REGEX_RIGHT.test(placement)) {
      return `
        border-top: 5px solid transparent;
        border-bottom: 5px solid transparent;
        border-right: 5px solid ${bgColor};
        left: -5px;
      `;
    }

    return '';
  }}
`;

export default function Popover(props) {
  const {
    disabled,
    children,
    content,
    flip,
    show,
    arrow,
    preventOverflow,
    offset,
    trigger,
    placement,
    isControlled,
    onChange = () => {/* empty function */},
  } = props;

  // declare hooks
  const [targetEl, setTargetEl] = useState(null);
  const [arrowEl, setArrowEl] = useState(null);
  const [popperEl, setPopperEl] = useState(null);
  const [showPopper, setShowPopper] = useState(show);
  const [portalEl, setPortalEl] = useState(null);
  const portalId = useRef('cb-popper-' + uuid());

  // create an effect that will manage trigger effects for the popover
  useEffect(() => {
    if (isControlled) {return;}

    function onMouseOver() {
      setShowPopper(true);
      onChange(true);
    }

    function onMouseOut() {
      setShowPopper(false);
      onChange(false);
    }

    function onMouseDown() {
      setShowPopper(!showPopper);
      onChange(!showPopper);
    }

    if (targetEl) {
      if (trigger === 'hover') {
        targetEl.addEventListener('mouseover', onMouseOver);
        targetEl.addEventListener('mouseout', onMouseOut);
      } else if (trigger === 'click') {
        targetEl.addEventListener('mousedown', onMouseDown);
      } else {
        throw new Error(`invalid trigger "${trigger}"`);
      }
    }

    return () => {
      if (targetEl) {
        if (trigger === 'hover') {
          targetEl.removeEventListener('mouseover', onMouseOver);
          targetEl.removeEventListener('mouseout', onMouseOut);
        } else if (trigger === 'click') {
          targetEl.removeEventListener('mousedown', onMouseDown);
        } else {
          throw new Error(`invalid trigger "${trigger}"`);
        }
      }
    };
  });

  // create and append the portal element
  useEffect(() => {
    const creating = document.createElement('div');
    creating.id = portalId.current;
    setPortalEl(creating);
    document.body.appendChild(creating);

    return () => {
      const removing = document.getElementById(portalId.current);
      if (removing) {
        document.body.removeChild(removing);
      }
    };
  }, [portalId.current]);

  // configure popper elements
  const {styles, attributes} = usePopper(targetEl, popperEl, {
    placement,
    modifiers: [
      {
        name: 'offset',
        enabled: !!offset,
        options: {offset},
      },
      {
        name: 'flip',
        enabled: !!flip,
        options: {fallbackPlacements: flip},
      },
      {
        name: 'preventOverflow',
        enabled: !!preventOverflow,
      },
      {
        name: 'arrow',
        enabled: !!arrow,
        options: {element: arrowEl},
      },
    ],
  });

  // if there is no content, the popover is disabled,
  // or the portal element does not exist, render the target normally
  if (!content || disabled === true || !portalEl) {
    return (
      <React.Fragment>
        {children}
      </React.Fragment>
    );
  }

  // else render the target inside a popper and attach portalled popper component
  return (
    <React.Fragment>
      <_TargetDiv ref={setTargetEl} {...props}>
        {children}
      </_TargetDiv>
      {((!isControlled && showPopper) || (isControlled && show)) && (
        ReactDOM.createPortal((
          <_PopperDiv
            className='cb-popover-popper'
            ref={setPopperEl}
            style={styles.popper}
            {...props}
            {...attributes.popper}>
            <_ArrowDiv
              className='cb-popover-arrow'
              style={styles.arrow}
              ref={setArrowEl}
              placement={attributes?.popper?.['data-popper-placement'] || placement}
              {...attributes.arrow}
            />
            <_PopperInnerDiv>
              {content}
            </_PopperInnerDiv>
          </_PopperDiv>
        ), portalEl)
      )}
    </React.Fragment>
  );
}

// create a re-usable prop-types object
const propTypes = {
  arrow: PropTypes.bool,
  show: PropTypes.bool,
  preventOverflow: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  onChange: PropTypes.func,
  offset: PropTypes.arrayOf(PropTypes.number),
  flip: PropTypes.arrayOf(PropTypes.string),
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  alignment: PropTypes.oneOf(['left', 'center', 'right']),
  trigger: PropTypes.oneOf(['hover', 'click']),
  placement: PropTypes.oneOf([
    'auto',
    'top',
    'top-start',
    'top-end',
    'right',
    'right-start',
    'right-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'left',
    'left-start',
    'left-end',
  ]),
  isControlled: PropTypes.bool,
  width: PropTypes.string,
};

Popover.propTypes = propTypes;

Popover.defaultProps = {
  show: false,
  arrow: true,
  preventOverflow: true,
  offset: [0, 5],
  trigger: 'hover',
  placement: 'top',
  maxWidth: '300px',
  isControlled: false,
  onChange: () => {/* empty function */},
};

/**
 * withPopover higher-order component
 */
export function withPopover(Cmp) {
  function withPopoverInner(props) {
    const {
      popoverConfig,
      ...others
    } = props;

    let config = popoverConfig;
    if (!popoverConfig) {
      config = {};
    } else if (typeof popoverConfig === 'string') {
      config = {content: popoverConfig};
    }

    return (
      <Popover {...config}>
        <Cmp {...others} />
      </Popover>
    );
  }

  withPopoverInner.propTypes = {
    ...Cmp.propTypes,
    popoverConfig: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape(propTypes),
    ]),
  };

  return withPopoverInner;
}
